// =============================================================================
// AGENT 2 — MISTRZ UI: EKRAN GRY
// Ładuje scenariusz z JSON, montuje GameBoard, wywołuje calculateScore po końcu.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { GameBoard } from '../../components/GameBoard/GameBoard';
import { useGame } from '../../store/gameStore';
import { calculateScore } from '../../engine/scoring';
import { shuffleArray } from '../../utils/shuffle';
import scenarioData from '../../data/scenario.json';
import type { Worker, Task, FinishMeta } from '../../types/models';

interface GameScreenProps {
  onGameEnd: () => void;
}

const workers = scenarioData.workers as Worker[];
const tasks = scenarioData.tasks as Task[];

export function GameScreen({ onGameEnd }: GameScreenProps) {
  const { state, requestFinish, endGame } = useGame();
  const hasCompletedRef = useRef(false);
  const [shuffledTasks, setShuffledTasks] = useState<Task[]>(() => {
    const main = tasks.filter((t) => t.priority !== 'awaria');
    const awarie = tasks.filter((t) => t.priority === 'awaria');
    return [...shuffleArray(main), ...shuffleArray(awarie)];
  });

  const performShuffle = useCallback(() => {
    const main = tasks.filter((t) => t.priority !== 'awaria');
    const awarie = tasks.filter((t) => t.priority === 'awaria');
    setShuffledTasks([...shuffleArray(main), ...shuffleArray(awarie)]);
  }, []);

  const handleFinishRequest = useCallback(
    (reason: 'time-up' | 'manual' | 'all-assigned', meta?: Partial<FinishMeta>) => {
      requestFinish(reason, meta);
    },
    [requestFinish],
  );

  useEffect(() => {
    if (state.phase !== 'playing') {
      hasCompletedRef.current = false;
    } else {
      // Re-shuffle on play start
      performShuffle();
    }
  }, [state.phase, performShuffle]);

  useEffect(() => {
    if (state.phase !== 'finishing' || hasCompletedRef.current) {
      return;
    }

    hasCompletedRef.current = true;
    const timeSpent = 90 - state.timeLeft;
    const result = calculateScore(
      state.assignments,
      shuffledTasks,
      workers,
      timeSpent,
      {
        ignoredAwariaByTaskId: state.finishMeta.ignoredAwariaByTaskId,
        excessTaskByTaskId: state.finishMeta.excessTaskByTaskId,
      },
    );
    endGame(result);
    onGameEnd();
  }, [
    state.phase,
    state.timeLeft,
    state.assignments,
    state.finishMeta.ignoredAwariaByTaskId,
    state.finishMeta.excessTaskByTaskId,
    shuffledTasks,
    endGame,
    onGameEnd,
  ]);

  return (
    <GameBoard
      tasks={shuffledTasks}
      workers={workers}
      onFinishRequest={handleFinishRequest}
    />
  );
}
